import "./style.scss";
import { useEffect, useState } from "react";
import ImageContainer from "@components/ImageContainer";
import HeroMobile from "./img/main-mobile.jpg";
import HeroDesktop from "./img/main-desktop.jpg";
import Button from "@components/Button";
import CopyIcon from "@assets/copy-icon.svg";
import PopupComponent from "@components/PopupComponent";
import Check from "@assets/check.svg";
import {
  useFloating,
  autoUpdate,
  offset,
  shift,
  useDismiss,
  useRole,
  useInteractions,
  autoPlacement,
} from "@floating-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { useWindowSize } from "../../../Helpers";

export default function RecommendVuse() {
  const { width } = useWindowSize();
  const XGA = width >= 1020;
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1020);
  const env = import.meta.env.VITE_BRANCH === "dev" ? "dev" : "prod";

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1020);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      autoPlacement({
        allowedPlacements: ["top-end", "bottom-end"],
      }),
      offset(15),
      shift({ padding: 10 }),
    ],
  });

  const role = useRole(context, { role: "tooltip" });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    useDismiss(context),
    role,
  ]);
  const data = queryClient.getQueryData(["user-details"]) ?? {};

  const referralCode = data?.consumer?.vusedeReferralCode;
  const linkToCopy = `${
    import.meta.env.VITE_VUSE_URL
  }/raf/index/index/voucherCode/${referralCode}`;
  const emailSubject = encodeURIComponent(
    "Rabatt auf den ersten Kauf eines Vuse Devices"
  );
  const emailBody = encodeURIComponent(
    `Mein persönlicher Rabatt-Code für dich: ${
      import.meta.env.VITE_VUSE_URL
    }/raf/index/index/voucherCode/${referralCode}`
  );

  async function share() {
    if (isMobile) {
      const shareData = {
        title: "Rabatt auf den ersten Kauf eines glo™ Devices",
        text: `Mein persönlicher Rabatt-Code für Dich: ${
          import.meta.env.VITE_VUSE_URL
        }/raf/index/index/voucherCode/${referralCode}`,
        url: linkToCopy,
      };

      try {
        await navigator.share(shareData);
      } catch (error) {
        if (isOpen === false) {
          navigator.clipboard.writeText(
            `${
              import.meta.env.VITE_VUSE_URL
            }/raf/index/index/voucherCode/${referralCode}`
          );
          setIsOpen(true);
          setTimeout(() => setIsOpen(false), 2500);
        }
        console.log(error);
      }
    }
  }

  return (
    <div className="recommend-vuse">
      <div className="hero">
        <ImageContainer desktopImg={HeroDesktop} mobileImg={HeroMobile}>
          <div className="inner-hero">
            <h2 className="title">
              <strong>-15€</strong>
              <br />
              für den Einkauf auf vuse.com/de
            </h2>
            <div className="copy-wrapper">
              {!referralCode ? (
                <div className="copy-card-empty">
                  <p className="copy-box-empty">
                    Jetzt auf vuse.com registrieren und deinen persönlichen
                    Empfehlungslink erhalten!
                  </p>
                  <Button
                    href={`${import.meta.env.VITE_VUSE_URL}/sign-in`}
                    btnClass="yellow"
                  >
                    Vuse.com besuchen
                  </Button>
                </div>
              ) : (
                <div className="copy-card">
                  <p className="copy-box-content">
                    Dein persönlicher Empfehlungslink:
                  </p>
                  <div className="copy-inner">
                    <PopupComponent
                      getReferenceProps={getReferenceProps}
                      isOpen={isOpen}
                      setIsOpen={setIsOpen}
                      refs={refs}
                      getFloatingProps={getFloatingProps}
                      floatingStyles={floatingStyles}
                    >
                      <div className="copy-tooltip">
                        <img src={Check} alt="Check" />
                        Code kopiert
                      </div>
                    </PopupComponent>
                    <div
                      ref={refs.setReference}
                      {...getReferenceProps()}
                      className="copy-box"
                    >
                      <div className="ref-link">
                        <label className="label"></label>
                        <input
                          className="link"
                          readOnly
                          value={`${
                            import.meta.env.VITE_VUSE_URL
                          }/raf/index/index/voucherCode/${referralCode}`}
                        />
                      </div>
                      <div
                        onClick={() => {
                          setIsOpen(true);
                          navigator.clipboard.writeText(linkToCopy);
                        }}
                        className="copy-plate"
                      >
                        <img src={CopyIcon} alt="Copy Icon" />
                      </div>
                    </div>
                    <div className="email-button-wrapper">
                      <Button
                        action={() => {
                          share();
                        }}
                        href={
                          isMobile
                            ? undefined
                            : `mailto:?subject=${emailSubject}&body=${emailBody}`
                        }
                        btnClass="outline yellow"
                      >
                        <div className="copy">
                          {XGA ? "Per E-Mail versenden" : "Teilen"}
                        </div>
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </ImageContainer>
      </div>
    </div>
  );
}
